import {
  AfterViewInit,
  Directive,
  ElementRef,
  inject,
  InjectionToken,
  Injector,
  Input,
  NgZone,
  OnDestroy,
  Type,
  ViewContainerRef,
} from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import {
  debounceTime,
  delay,
  distinctUntilChanged,
  fromEvent,
  interval,
  map,
  Observable,
  pairwise,
  tap,
  withLatestFrom,
} from 'rxjs';
import {
  SnapScrollerPage,
  SnapScrollerStore,
} from './snap-scroller.store.service';
import { ContentObserver } from '@angular/cdk/observers';

export const SNAP_SCROLLER_CONTROLS = new InjectionToken<
  () => Promise<Type<any>>
>('SNAP_SCROLLER_CONTROLS');

interface SnapScrollerDirectiveState {
  scrollWidth?: number;
  scrollHeight?: number;
}

@Directive({
  selector: '[nxMassaudSnapScroller]',
  exportAs: 'snapScroller',
  standalone: true,
  providers: [SnapScrollerStore],
})
export class SnapScrollerDirective
  extends ComponentStore<SnapScrollerDirectiveState>
  implements OnDestroy, AfterViewInit
{
  private el: ElementRef<HTMLElement> = inject(ElementRef<HTMLElement>);
  private snapScrollerStore = inject(SnapScrollerStore);

  private zone = inject(NgZone);

  private viewContainer = inject(ViewContainerRef);
  private injector = inject(Injector);

  private contentObserver = inject(ContentObserver);

  private snapScrollerControls = inject(SNAP_SCROLLER_CONTROLS, {
    optional: true,
  });

  private pixelTolerance = 2;

  readonly pageCount$ = this.snapScrollerStore.pageCount$;
  readonly index$ = this.snapScrollerStore.index$;
  readonly pages$ = this.snapScrollerStore.pages$;
  readonly canPrevious$ = this.snapScrollerStore.canPrevious$;
  readonly canNext$ = this.snapScrollerStore.canNext$;

  private readonly scrollWidth$ = this.select((s) => s.scrollWidth);

  #clickListeners!: { el: HTMLElement; function: any }[];

  private onScroll = this.effect(() => {
    return fromEvent(this.el.nativeElement, 'scroll')
      .pipe(debounceTime(800))
      .pipe(
        withLatestFrom(this.pageCount$, this.pages$),
        tap(([event, pageCount, pages]) => {
          const { nativeElement } = this.el;
          const { offsetWidth, scrollLeft, scrollWidth } = nativeElement;
          let position = 0;
          let scrollLeftPlusOffsetWidth = Math.round(scrollLeft) + offsetWidth;
          // console.log({ scrollLeftPlusOffsetWidth });
          if (scrollLeft === 0) {
            position = pages[0].position;
          } else if (
            scrollLeftPlusOffsetWidth >= scrollWidth - this.pixelTolerance &&
            scrollLeftPlusOffsetWidth <= scrollWidth + this.pixelTolerance
          ) {
            position = Math.round(scrollLeft + offsetWidth);
          } else {
            position = Math.round(scrollLeft + offsetWidth / 2);
          }
          /*if (this.containerId === 'projects') {
          console.log('SnapScrollerDirective', { pageCount });
        }*/
          if (pageCount) {
            const index = pages.findIndex(
              (p) =>
                p.position >= position - this.pixelTolerance &&
                p.position <= position + this.pixelTolerance
            );
            if (index > -1) {
              console.log({ index });
              console.log('pages.length', pages.length);
              this.snapScrollerStore.patchState({
                index: Math.min(index, pages.length - 1),
              });
            }
          }
        })
      );
  });

  private setIndex = this.effect(() => {
    return this.snapScrollerStore.currentPage$.pipe(
      pairwise(),
      tap(([oldPage, page]) => {
        /*console.log({ oldPage });
        console.log({ page });*/
        if (oldPage) {
          //play or pause media element, only managed by vimeo media at the moment
          oldPage.els.forEach((e) => {
            e.setAttribute('playback', 'pause');
          });
        }
        if (page) {
          const scrollPosition = page.position;
          //page.el.scrollIntoView({behavior: 'smooth'});
          const { nativeElement } = this.el;
          const { offsetWidth } = nativeElement;
          if (page.els) {
            //play or pause media element, only managed by vimeo media at the moment
            page.els.forEach((e) => {
              e.setAttribute('playback', 'play');
            });
          }
          nativeElement.scrollTo({
            left: scrollPosition - offsetWidth / 2,
            behavior: 'smooth',
          });
        }
      })
    );
  });

  private contentObserve = this.effect(
    (contentObserve$: Observable<MutationRecord[]>) => {
      return contentObserve$.pipe(
        delay(400),
        tap((result) => {
          const childListOnly = result.filter(
            (r) => r.type === 'childList' && r.target.nodeName !== '#comment'
          );
          const snapScrollOnly = childListOnly.filter((r: MutationRecord) =>
            this.elementHasSnapScroll(r.target as HTMLElement)
          );
          if (snapScrollOnly.length > 0) {
            this.processSnapPoints();
          }
          // this.processSnapPoints();
        })
      );
    }
  );

  private enabled = this.effect(() => {
    return this.snapScrollerStore.enabled$.pipe(
      tap((enabled) => {
        if (enabled) {
          console.log({ enabled });
          /*if (this.snapScrollerControls) {
            from(this.snapScrollerControls())
              .pipe(take(1))
              .subscribe((component) => {
                if (component) {
                  const viewContainer = controlsTarget ?? this.viewContainer;
                  console.log({ controlsTarget });
                  const componentRef = viewContainer.createComponent<any>(component, {
                    injector: this.injector,
                  });
                  componentRef.changeDetectorRef.detectChanges();
                }
              });
          }*/

          // this.contentObserve(this.contentObserver.observe(this.el));

          /*this.#resizeObserver = new ResizeObserver((entries) => {
            this.processSnapPoints();
          });
          this.#resizeObserver.observe(this.el.nativeElement);*/

          this.patchState(
            interval(200).pipe(
              map(() => {
                const scrollWidth = this.el.nativeElement.scrollWidth;
                return { scrollWidth };
              })
            )
          );
        }
      })
    );
  });

  setScrollWidth = this.effect(() => {
    return this.scrollWidth$.pipe(
      distinctUntilChanged(),
      tap((scrollWidth) => {
        if (scrollWidth) {
          this.processSnapPoints();
          this.el.nativeElement.style.scrollSnapType = 'x mandatory';
        }
      })
    );
  });

  #resizeObserver!: ResizeObserver;

  @Input()
  set enable(enable: boolean) {
    if (enable) {
      this.snapScrollerStore.enable();
    }
  }

  @Input()
  containerId: string | undefined;

  constructor() {
    super({});
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.disable();
    this.cleanListeners();
  }

  ngAfterViewInit(): void {}

  notEmpty<TValue>(value: TValue | null | undefined): value is TValue {
    return value !== null && value !== undefined;
  }

  cleanListeners() {
    if (this.#clickListeners) {
      this.#clickListeners.forEach((c) => {
        c.el.removeEventListener('click', c.function);
      });
    }
  }

  processSnapPoints() {
    this.cleanListeners();
    this.#clickListeners = [];
    const { nativeElement } = this.el;
    let snapableElements: HTMLElement[] = (
      Array.from(nativeElement.querySelectorAll(':scope li')) as HTMLElement[]
    ).filter((el: HTMLElement) => {
      return this.elementHasSnapScroll(el);
    }) as HTMLElement[];
    console.log({ snapableElements });
    const parentRect = nativeElement.getBoundingClientRect();
    const parentScrollWidth = nativeElement.scrollWidth;
    // console.log('firstPage parentLeft', { nativeElement });
    const parentLeft = Math.round(parentRect.left);
    const positions = new Map<number, SnapScrollerPage>();
    const pages: SnapScrollerPage[] = snapableElements
      .map((el, index) => {
        const elRect = el.getBoundingClientRect();
        const scrollSnapAlign = getComputedStyle(el).scrollSnapAlign;
        console.log('processSnapPoints()', { scrollSnapAlign });
        const origin = parentRect.left - elRect.left;
        let position = elRect.left - parentLeft;
        // console.log('1', { position });
        /*if (scrollSnapAlign === 'center') {
          position = position + elRect.width / 2;
        } else if (scrollSnapAlign === 'end') {
          position = position + elRect.width;
        }*/
        //impose center scrollSnap, later we impose start to first item, and end to last item;
        el.style.scrollSnapAlign = 'center';
        position = Math.round(position + elRect.width / 2);
        /*console.log('2 elRect.width', elRect.width);
        console.log('2', { position });*/
        let snapScrollerPage = { position: position, els: [el] };
        const positionInMap = positions.get(position);
        if (!positionInMap) {
          const positionsSize = positions.size;
          positions.set(position, snapScrollerPage);
          const elementClickFunction = this.onElementClick.bind(
            this,
            positionsSize
          );
          el.addEventListener('click', elementClickFunction);
          this.#clickListeners.push({ el, function: elementClickFunction });
          return snapScrollerPage;
        } else if (positionInMap) {
          snapScrollerPage = positionInMap as SnapScrollerPage;
          snapScrollerPage.els.push(el);
        }
        return null;
      })
      .filter(this.notEmpty)
      .sort((a, b) => a.position - b.position);
    if (pages.length > 0) {
      const firstPage = pages[0];
      const firstPageElement = firstPage.els[0];
      const elRect = firstPageElement.getBoundingClientRect();
      firstPage.position = Math.round(Math.abs(parentLeft - elRect.left));
      /*if (this.containerId === 'projects') {
        console.log('SnapScrollerDirective firstPage parentLeft', parentLeft);
        console.log('SnapScrollerDirective firstPage elRect.left', elRect.left);
        console.log('SnapScrollerDirective firstPage', firstPage);
        console.log(
          'SnapScrollerDirective firstPage.position',
          firstPage.position
        );
      }*/
      firstPageElement.style.scrollSnapAlign = 'start';
    }
    if (pages.length > 1) {
      const lastPage = pages[pages.length - 1];
      const lastPageElement = lastPage.els[0];
      const elRect = lastPageElement.getBoundingClientRect();
      lastPage.position = Math.round(
        Math.abs(parentLeft - elRect.left) + elRect.width
      );
      lastPageElement.style.scrollSnapAlign = 'end';
    }
    /*console.log('SnapScrollerDirective this.containerId', this.containerId);
    if (this.containerId === 'projects') {
      console.log('SnapScrollerDirective', { pages });
    }*/
    this.snapScrollerStore.patchState({
      pages,
    });
  }

  onElementClick(index: number) {
    this.snapScrollerStore.goToIndex(index);
  }

  private elementHasSnapScroll(el: HTMLElement): boolean {
    return (
      !!getComputedStyle(el).scrollSnapAlign &&
      getComputedStyle(el).scrollSnapAlign !== 'none'
    );
  }

  disable() {
    if (this.#resizeObserver) {
      this.#resizeObserver.unobserve(this.el.nativeElement);
    }
  }

  goToIndex(index: number) {
    this.snapScrollerStore.goToIndex(index);
  }

  previous() {
    console.log('previous()');
    this.snapScrollerStore.previous();
  }

  next() {
    this.snapScrollerStore.next();
  }
}
